import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  Radio,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import HelpIcon from "@mui/icons-material/Help";
import { formatarCPFCNPJ } from "../../helpers/masks";
import { useQuery } from "react-query";
import axiosClient from "../../lib/axios";
import useDebounce from "../../lib/useDebounce";
import BtnCarregarDados from "../../components/BtnCarregarDados";
import useGenericDebounce from "../../lib/useGenericDebounce";
import useMobileScreen from "../../lib/useMobileScreen";
import DialogAppBar from "../../components/DialogAppBar";
import {
  IDataPublicos,
  TFazendaPublico,
} from "../../types/api/geral/pesquisar-publicos";
import { DialogSelecionarFazenda } from "./DialogSelecionarFazenda";

interface DialogContentSelecionarClienteProps {
  retornarProcessos?: boolean;
  retornarFazendas?: boolean;
  defaultValue: IDataPublicos | IDataPublicos[];
  defaultFiltroTipoClient?: string[];
  extraParams?: { [key: string]: any };
  handleClose: () => void;
  onChange: (arg: IDataPublicos | IDataPublicos[]) => void;
  multiple?: boolean;
  label?: string;
  showAllTypes?: boolean;
}

export default function DialogContentSelecionarCliente({
  defaultValue,
  defaultFiltroTipoClient = [],
  retornarProcessos = false,
  retornarFazendas = false,
  extraParams = {},
  onChange,
  handleClose,
  multiple = false,
  label = "Público",
  showAllTypes = false
}: DialogContentSelecionarClienteProps) {

  const telaMobile = useMobileScreen();
  const [limit, setLimit] = useState(20);
  const [palavraChave, setPalavraChave] = useState("");
  const [selecionados, setSelecionados] = useState<IDataPublicos[]>(
    multiple
      ? [...(defaultValue as IDataPublicos[])]
      : [defaultValue as IDataPublicos]
  );
  const [filtroTipoClient, setFiltroTipoCliente] = useState(
    defaultFiltroTipoClient
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const [openFazendaModal, setOpenFazendaModal] = useState(false);
  const [listarBloqueados, setListarBloqueados] = useState(false);

  const tiposCliente = useMemo(
    () => [
      { label: "Associado", valor: "A" },
      { label: "Cliente", valor: "C" },
      { label: "Fornecedor", valor: "F" },
      { label: "Empregado", valor: "E" },
    ],
    []
  );

  const debouncedKeyword = useDebounce(palavraChave);
  const debouncedFiltro = useGenericDebounce(filtroTipoClient);

  const { data, isFetching, isLoading } = useQuery({
    queryKey: ["pesquisar-publicos", debouncedKeyword, limit, debouncedFiltro, listarBloqueados],
    queryFn: () =>
      axiosClient.get("/pesquisar-publicos", {
        params: {
          limit,
          page: 1,
          retornar_processos: retornarProcessos,
          retornar_fazendas: retornarFazendas,
          ...extraParams,
          pesquisar: debouncedKeyword,
          tipo: debouncedFiltro,
          listar_bloqueados: listarBloqueados,
        },
      }),
    cacheTime: 0,
    staleTime: 0,
    onSuccess: () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    },
  });

  const { requestClientes, dataClientes } = useMemo(
    () => ({
      requestClientes: data?.data,
      dataClientes: data?.data.data,
    }),
    [data]
  );

  // Funções Manipuladoras
  const handleClick = useCallback(
    (item: IDataPublicos) => {
      if (multiple) {
        const index = selecionados.findIndex(
          (i) => i.codigo_publico === item.codigo_publico
        );
        if (index === -1) {
          setSelecionados((prevState) => [...prevState, item]);
        } else {
          setSelecionados((prevState) =>
            prevState.filter((_, i) => i !== index)
          );
        }
      }

      if (!multiple) setSelecionados([item]);
    },
    [selecionados, multiple]
  );

  const handleSubmit = useCallback(() => {
    if (retornarFazendas && selecionados[0]?.fazendas?.length > 0) {
      setOpenFazendaModal(true);
      return;
    }

    if (!multiple) {
      onChange(selecionados[0]);
      return;
    }

    onChange(selecionados);
  }, [onChange, selecionados, multiple, retornarFazendas]);

  const handleCheckFiltro = useCallback(
    (valor: string) => {
      const cloneFiltro = [...filtroTipoClient];
      const index = cloneFiltro.indexOf(valor);
      if (index !== -1) cloneFiltro.splice(index, 1);
      if (index === -1) cloneFiltro.push(valor);
      setFiltroTipoCliente(cloneFiltro);
    },
    [filtroTipoClient]
  );

  const handleFazendaSelect = useCallback(
    (selectedFazenda: TFazendaPublico | null) => {
      setOpenFazendaModal(false);

      if (!multiple) {
        onChange({
          ...selecionados[0],
          selected_fazenda: selectedFazenda,
        });
        return;
      }

      onChange(
        selecionados.map((item) => ({
          ...item,
          selected_fazenda: selectedFazenda,
        }))
      );
    },
    [onChange, selecionados, multiple]
  );

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <>
      {telaMobile && (
        <DialogAppBar {...{ handleClose }} title="Selecionar cliente" />
      )}

      <DialogTitle>Selecione o {label}</DialogTitle>

      <DialogContent sx={{ mt: { xs: 2, md: 0 } }}>
        <Box display="flex" alignItems="center" gap={2} py={2}>
          <TextField
            value={palavraChave}
            fullWidth
            inputRef={inputRef}
            label="Pesquisar"
            slotProps={{
              htmlInput: { textTransform: "uppercase", tabIndex: 0 },
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                )
              }
            }}
            onChange={(e) => setPalavraChave(e.target.value)}
          />
        </Box>

        {(showAllTypes || defaultFiltroTipoClient.length === 0) && (
          <Box display="flex" mb={1} justifyContent="space-between" flexWrap="wrap">
            {tiposCliente.map((item, index) => {
              const isChecked = filtroTipoClient.includes(item.valor);
              if (!showAllTypes && defaultFiltroTipoClient.length > 0 && !defaultFiltroTipoClient.includes(item.valor)) {
                return null;
              }
              return (
                <FormControlLabel
                  key={index}
                  label={item.label}
                  control={
                    <Checkbox
                      checked={isChecked}
                      size="small"
                      disabled={!showAllTypes && defaultFiltroTipoClient.length > 0}
                    />
                  }
                  onChange={() => handleCheckFiltro(item.valor)}
                />
              );
            })}
          </Box>
        )}

        <Stack direction={'row'} justifyContent={'flex-start'}>
          <FormControlLabel
            control={
              <Switch
                checked={listarBloqueados}
                onChange={() => setListarBloqueados((prev) => !prev)}
              />
            }
            label="Listar bloqueados"
            sx={{ mb: 2 }}
          />
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {isLoading && (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress />
          </Box>
        )}

        {!isLoading && (
          <FormGroup>
            <Box
              display="flex"
              overflow="auto"
              height={{ xs: 550, md: 350 }}
              flexDirection="column"
            >
              {dataClientes && dataClientes.length <= 0 && (
                <Box
                  display="flex"
                  justifyContent="center"
                  paddingY={2}
                  gap={1}
                  color="gray"
                >
                  <HelpIcon />
                  <Typography>Nenhum resultado encontrado</Typography>
                </Box>
              )}

              {dataClientes &&
                dataClientes.length > 0 &&
                dataClientes.map((i: IDataPublicos, index: number) => {
                  const isChecked = selecionados.some(
                    (item) =>
                      `${item.tipo_publico}${item.codigo_publico}` ===
                      `${i.tipo_publico}${i.codigo_publico}`
                  );
                  return (
                    <FormControlLabel
                      key={index}
                      onChange={() => handleClick(i)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleClick(i);
                        }
                      }}
                      control={
                        multiple ? (
                          <Checkbox checked={isChecked} />
                        ) : (
                          <Radio checked={isChecked} />
                        )
                      }
                      sx={{ mb: 2 }}
                      label={
                        <Box>
                          <Typography fontWeight={600}>
                            {`${i.tipo_publico + i.codigo_publico} - ${i.nome_pessoa
                              }`}
                          </Typography>
                          <Typography>
                            {formatarCPFCNPJ(i.documento_pessoa)}
                          </Typography>
                        </Box>
                      }
                    />
                  );
                })}
              <BtnCarregarDados
                {...{ limit, isFetching }}
                total={requestClientes.total}
                action={() => setLimit((prevState) => prevState + 20)}
              />
            </Box>
          </FormGroup>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button
          autoFocus
          variant="contained"
          onClick={handleSubmit}
          disabled={
            selecionados?.[0].tipo_publico === "" &&
            selecionados?.[0].codigo_publico === ""
          }
        >
          Avançar
        </Button>
      </DialogActions>

      {openFazendaModal && (
        <DialogSelecionarFazenda
          open={openFazendaModal}
          handleClose={() => setOpenFazendaModal(false)}
          fazendas={selecionados[0].fazendas}
          onSelect={handleFazendaSelect}
        />
      )}
    </>
  );
}
